import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  FaExclamationCircle,
  FaLock,
  FaLockOpen,
  FaRedo,
} from "react-icons/fa";

import { useAuth } from "../../Authentication/AuthContext";

import Login from "../../Authentication/Login/Login";

import SubscriptionMonthly from "../../Authentication/Subscription/SubscriptionMonthly";

import "./BooksPageStyles.css";

type BookLevelKey = "A1" | "A2" | "B1" | "B2" | "C1" | "C2";

type DisplayBook = {
  level: BookLevelKey;
  bookId: number;
  title: string;
  fileName: string;
  isUnlocked: boolean;
};

type DisplayBookLevel = {
  level: BookLevelKey;
  title: string;
  subtitle: string;
  unlockedCount: number;
  books: DisplayBook[];
};

const ALL_BOOK_LEVELS: BookLevelKey[] = ["A1", "A2", "B1", "B2", "C1", "C2"];

const LEVEL_META: Record<
  BookLevelKey,
  {
    title: string;
    subtitle: string;
  }
> = {
  A1: {
    title: "Anfänger",
    subtitle: "",
  },

  A2: {
    title: "Grundlagen",
    subtitle: "",
  },

  B1: {
    title: "Mittelstufe",
    subtitle: "",
  },

  B2: {
    title: "Oberstufe",
    subtitle: "",
  },

  C1: {
    title: "Profi",
    subtitle: "",
  },

  C2: {
    title: "Experte",
    subtitle: "",
  },
};

const normalizeBookLevel = (value: unknown): BookLevelKey | null => {
  const cleanValue = String(value || "")
    .trim()
    .toUpperCase();

  return ALL_BOOK_LEVELS.includes(cleanValue as BookLevelKey)
    ? (cleanValue as BookLevelKey)
    : null;
};

const buildBookPath = (book: DisplayBook): string => {
  return `/book-view?level=${encodeURIComponent(
    book.level,
  )}&bookId=${encodeURIComponent(
    String(book.bookId),
  )}&book=${encodeURIComponent(book.title)}&file=${encodeURIComponent(
    book.fileName,
  )}`;
};

const createBooksForLevel = (
  level: BookLevelKey,
  isUnlocked: boolean,
): DisplayBook[] => {
  return Array.from(
    { length: 10 },

    (_, index) => {
      const bookId = index + 1;

      return {
        level,
        bookId,

        title: `Deutsch ${level}.${bookId}`,

        fileName: `${level}_${bookId}.html`,

        isUnlocked,
      };
    },
  );
};

const BooksPage: React.FC = () => {
  const navigate = useNavigate();

  const {
    isLoggedIn,
    authLoading,
    accessChecked,
    accessLoading,
    accessError,
    hasFullBookAccess,
    accessibleBookLevels,
    refreshAccess,
  } = useAuth();

  const [showLoginModal, setShowLoginModal] = useState<boolean>(false);

  const [showSubscriptionModal, setShowSubscriptionModal] =
    useState<boolean>(false);

  const [pendingBook, setPendingBook] = useState<DisplayBook | null>(null);

  const [manualRefreshing, setManualRefreshing] = useState<boolean>(false);

  useEffect(() => {
    document.documentElement.classList.add("books-screen-page");

    document.body.classList.add("books-screen-page");

    document.documentElement.classList.remove("book-view-screen-page");

    document.body.classList.remove("book-view-screen-page");

    return () => {
      document.documentElement.classList.remove("books-screen-page");

      document.body.classList.remove("books-screen-page");
    };
  }, []);

  /**
   * BooksPage does not call Laravel here.
   *
   * It only consumes the already checked
   * global access from AuthContext.
   */
  const unlockedLevelSet = useMemo(() => {
    const levels = new Set<BookLevelKey>();

    accessibleBookLevels.forEach((level) => {
      const normalizedLevel = normalizeBookLevel(level);

      if (normalizedLevel) {
        levels.add(normalizedLevel);
      }
    });

    if (hasFullBookAccess) {
      ALL_BOOK_LEVELS.forEach((level) => levels.add(level));
    }

    return levels;
  }, [accessibleBookLevels, hasFullBookAccess]);

  const visibleBookLevels = useMemo<DisplayBookLevel[]>(() => {
    return ALL_BOOK_LEVELS.map((level) => {
      const isLevelUnlocked = Boolean(
        isLoggedIn && (hasFullBookAccess || unlockedLevelSet.has(level)),
      );

      const books = createBooksForLevel(level, isLevelUnlocked);

      return {
        level,

        title: LEVEL_META[level].title,

        subtitle: LEVEL_META[level].subtitle,

        unlockedCount: isLevelUnlocked ? books.length : 0,

        books,
      };
    });
  }, [hasFullBookAccess, isLoggedIn, unlockedLevelSet]);

  const totalVisibleBooks = useMemo(() => {
    return visibleBookLevels.reduce(
      (total, group) => total + group.books.length,

      0,
    );
  }, [visibleBookLevels]);

  const totalUnlockedBooks = useMemo(() => {
    return visibleBookLevels.reduce(
      (total, group) => total + group.unlockedCount,

      0,
    );
  }, [visibleBookLevels]);

  const hasKnownAccessState = Boolean(
    accessChecked || hasFullBookAccess || accessibleBookLevels.length > 0,
  );

  /**
   * A fresh AuthContext cache makes this false immediately
   * after Firebase restores the user.
   *
   * No individual book spinners are required.
   */
  const isInitialAccessLoading = Boolean(
    authLoading || (isLoggedIn && !hasKnownAccessState && accessLoading),
  );

  const closeModals = useCallback((): void => {
    setShowLoginModal(false);

    setShowSubscriptionModal(false);
  }, []);

  const openBook = useCallback(
    (book: DisplayBook): void => {
      if (authLoading || isInitialAccessLoading) {
        return;
      }

      if (!isLoggedIn) {
        setPendingBook(book);

        setShowSubscriptionModal(false);

        setShowLoginModal(true);

        return;
      }

      if (!book.isUnlocked) {
        setPendingBook(book);

        setShowLoginModal(false);

        setShowSubscriptionModal(true);

        return;
      }

      closeModals();

      setPendingBook(null);

      navigate(buildBookPath(book));

      window.requestAnimationFrame(() => {
        window.scrollTo({
          top: 0,
          left: 0,
          behavior: "auto",
        });
      });
    },
    [authLoading, closeModals, isInitialAccessLoading, isLoggedIn, navigate],
  );

  /**
   * After login, use the already updated AuthContext state.
   * Do not create another protected-books request.
   */
  useEffect(() => {
    if (
      !pendingBook ||
      !isLoggedIn ||
      authLoading ||
      isInitialAccessLoading ||
      showLoginModal ||
      showSubscriptionModal
    ) {
      return;
    }

    const currentBook = visibleBookLevels
      .flatMap((group) => group.books)
      .find(
        (book) =>
          book.level === pendingBook.level &&
          book.bookId === pendingBook.bookId,
      );

    if (!currentBook) {
      setPendingBook(null);
      return;
    }

    if (currentBook.isUnlocked) {
      setPendingBook(null);

      navigate(buildBookPath(currentBook));

      return;
    }

    setShowSubscriptionModal(true);
  }, [
    authLoading,
    isInitialAccessLoading,
    isLoggedIn,
    navigate,
    pendingBook,
    showLoginModal,
    showSubscriptionModal,
    visibleBookLevels,
  ]);

  /**
   * This is the only normal manual refresh action.
   * Opening or reloading BooksPage does not call it.
   */
  const handleManualRefresh = useCallback(async (): Promise<void> => {
    if (!isLoggedIn || manualRefreshing) {
      return;
    }

    setManualRefreshing(true);

    try {
      await refreshAccess(true);
    } finally {
      setManualRefreshing(false);
    }
  }, [isLoggedIn, manualRefreshing, refreshAccess]);

  /**
   * A completed website Premium purchase must force
   * one fresh check because the entitlement just changed.
   */
  const handleSubscriptionCompleted = useCallback(async (): Promise<void> => {
    setShowSubscriptionModal(false);

    setManualRefreshing(true);

    try {
      const nextAccess = await refreshAccess(true);

      if (!pendingBook) {
        return;
      }

      const canOpenPendingBook = Boolean(
        nextAccess.hasFullBookAccess ||
        nextAccess.accessibleBookLevels.includes(pendingBook.level),
      );

      if (canOpenPendingBook) {
        const nextBook = pendingBook;

        setPendingBook(null);

        navigate(buildBookPath(nextBook));
      }
    } finally {
      setManualRefreshing(false);
    }
  }, [navigate, pendingBook, refreshAccess]);

  const renderCardParticles = (): React.ReactNode => {
    return (
      <div className="book-card-particles" aria-hidden="true">
        <span />
        <span />
        <span />
        <span />
        <span />
        <span />
      </div>
    );
  };

  const headerTitle = hasFullBookAccess
    ? "Alle Deutschbücher"
    : isLoggedIn
      ? "Meine Deutschbücher"
      : "Deutschbücher";

  const headerSubtitle = !isLoggedIn
    ? "Alle Buchtitel sind sichtbar. Zum Öffnen bitte einloggen und den Premium-Zugang abonnieren."
    : hasFullBookAccess
      ? "Vollzugang: Alle Bücher von A1 bis C2 sind freigeschaltet."
      : "Freigeschaltete Bücher können geöffnet werden. Gesperrte Bücher benötigen Premium-Zugang.";

  return (
    <div className="books-page">
      <Login
        isOpen={showLoginModal}
        onClose={() => {
          setShowLoginModal(false);
        }}
      />

      <SubscriptionMonthly
        isOpen={showSubscriptionModal}
        onClose={() => {
          setShowSubscriptionModal(false);

          setPendingBook(null);
        }}
        onSubscribed={handleSubscriptionCompleted}
      />

      <section className="books-container">
        <header className="books-header-card">
          <div className="books-header-content">
            <p className="books-kicker">Deutschbibliothek</p>

            <h1 className="books-page-title">{headerTitle}</h1>

            <p className="books-subtitle">{headerSubtitle}</p>
          </div>

          <div className="books-header-badge">
            <strong>
              {isInitialAccessLoading
                ? "..."
                : `${totalUnlockedBooks}/${totalVisibleBooks}`}
            </strong>

            <span>Freigeschaltete Bücher</span>
          </div>
        </header>

        {accessError && !hasKnownAccessState && (
          <article className="book-group book-status-group">
            {renderCardParticles()}

            <div className="book-card-badge book-card-badge-status">
              <section className="book-card-left">
                <div className="book-lock-block">
                  <span className="book-level-icon book-level-icon-compact book-level-icon-locked">
                    <FaExclamationCircle className="book-access-icon book-level-access-icon" />
                  </span>

                  <span className="book-lock-status">Fehler</span>
                </div>

                <div className="book-title-block">
                  <h2>!</h2>

                  <p>Zugang</p>
                </div>
              </section>

              <section className="book-card-right book-card-right-message">
                <button
                  type="button"
                  className="book-button book-message-button"
                  onClick={() => {
                    void handleManualRefresh();
                  }}
                >
                  <span className="book-name">
                    {accessError || "Zugang erneut prüfen"}
                  </span>

                  <span className="book-arrow book-arrow-compact book-arrow-locked">
                    <FaRedo />
                  </span>
                </button>
              </section>
            </div>
          </article>
        )}

        <div className="books-learning-grid">
          {visibleBookLevels.map((group) => {
            const isLevelUnlocked = group.unlockedCount > 0;

            const cardStatusClassName = isInitialAccessLoading
              ? "book-card-checking"
              : isLevelUnlocked
                ? "book-card-unlocked"
                : "book-card-locked";

            const levelIconClassName = isInitialAccessLoading
              ? "book-level-icon-checking"
              : isLevelUnlocked
                ? "book-level-icon-unlocked"
                : "book-level-icon-locked";

            const statusText = isInitialAccessLoading
              ? "Laden"
              : isLevelUnlocked
                ? "Freigeschaltet"
                : "Gesperrt";

            const countText = isInitialAccessLoading
              ? "Gespeicherter Zugang wird geladen"
              : `${group.unlockedCount}/${group.books.length} freigeschaltet`;

            return (
              <article
                key={group.level}
                className={`book-group book-${group.level.toLowerCase()} ${cardStatusClassName}`}
              >
                {renderCardParticles()}

                <div className="book-card-badge">
                  <section className="book-card-left">
                    <div className="book-lock-block">
                      <span
                        className={`book-level-icon book-level-icon-compact ${levelIconClassName}`}
                      >
                        {isLevelUnlocked ? (
                          <FaLockOpen className="book-access-icon book-level-access-icon" />
                        ) : (
                          <FaLock className="book-access-icon book-level-access-icon" />
                        )}
                      </span>

                      <span className="book-lock-status">{statusText}</span>
                    </div>

                    <div className="book-title-block">
                      <h2>{group.level}</h2>

                      <p>{group.title}</p>

                      <small>{countText}</small>
                    </div>
                  </section>

                  <section className="book-card-right">
                    <ul className="book-list">
                      {group.books.map((book) => {
                        const arrowClassName = isInitialAccessLoading
                          ? "book-arrow-checking"
                          : book.isUnlocked
                            ? "book-arrow-unlocked"
                            : "book-arrow-locked";

                        return (
                          <li
                            key={`${book.level}-${book.bookId}`}
                            className="book-item"
                          >
                            <button
                              type="button"
                              className={`book-button ${
                                book.isUnlocked ? "" : "book-button-locked"
                              }`}
                              onClick={() => openBook(book)}
                              disabled={isInitialAccessLoading}
                              aria-label={`${book.title} öffnen`}
                            >
                              <span className="book-name">{book.title}</span>

                              <span
                                className={`book-arrow book-arrow-compact ${arrowClassName}`}
                              >
                                {book.isUnlocked ? (
                                  <FaLockOpen className="book-access-icon book-item-access-icon" />
                                ) : (
                                  <FaLock className="book-access-icon book-item-access-icon" />
                                )}
                              </span>
                            </button>
                          </li>
                        );
                      })}
                    </ul>
                  </section>
                </div>
              </article>
            );
          })}
        </div>
      </section>
    </div>
  );
};

export default BooksPage;
